import { Box, Typography, Alert, CircularProgress } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Helmet } from "react-helmet-async";
import Api from "../utils/api";
import React, { useState, useEffect } from "react";

const AdminPayments = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");

  useEffect(() => {
    const fetchPayments = async () => {
      try {
        const data = await Api.get("admin/payments");
        setPayments(data);
      } catch (err) {
        setError(err.message || "Failed to fetch payments");
      } finally {
        setLoading(false);
      }
    };
    fetchPayments();
  }, []);

  // Get unique statuses for filter dropdowns
  const uniqueStatuses = Array.from(
    new Set(payments.map((p) => p.status).filter(Boolean)),
  );
  // Payment types from backend model
  const paymentTypes = [
    "admission",
    "monthly",
    "special-chance",
    "qr_code",
    "cash",
  ];

  // Filtering logic
  const filteredRows = payments
    .filter((p) => {
      if (filterStatus && p.status !== filterStatus) return false;
      if (filterType && p.type !== filterType) return false;
      return true;
    })
    .map((p) => ({
      ...p,
      id: p.id,
      learnerName: p.learnerName || "-",
      enrollmentId: p.enrollmentId || "-",
      type: p.type || "-",
      amount: p.amount || "-",
      currency: p.currency || "-",
      status: p.status || "-",
      transactionId: p.transactionId || "-",
      createdAt: p.createdAt ? new Date(p.createdAt).toLocaleString() : "-",
    }));

  return (
    <>
      <Helmet>
        <title>AKAAL KIDS - Admin Panel | Payments</title>
      </Helmet>
      <Box
        sx={{
          mt: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          margin: "0 auto",
        }}
      >
        <Typography variant="h4" gutterBottom sx={{ mb: 2 }}>
          All Payments
        </Typography>
        {loading && (
          <Box sx={{ width: "100%", textAlign: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {!loading && !error && (
          <>
            <Box sx={{ mb: 2, width: "100%" }}>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Box sx={{ minWidth: 180 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Filter by Status
                  </Typography>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="admin-btn"
                  >
                    <option value="">All</option>
                    {uniqueStatuses.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </Box>
                <Box sx={{ minWidth: 180 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Filter by Payment Type
                  </Typography>
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="admin-btn"
                  >
                    <option value="">All</option>
                    {paymentTypes.map((type) => (
                      <option key={type} value={type}>
                        {type
                          .replace(/_/g, " ")
                          .replace(/\b\w/g, (c) => c.toUpperCase())}
                      </option>
                    ))}
                  </select>
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                width: "100%",
                border: "2px solid #110f0fff",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <DataGrid
                autoHeight
                rows={filteredRows}
                columns={[
                  { field: "id", headerName: "ID", flex: 0.5 },
                  { field: "learnerName", headerName: "User", flex: 1 },
                  { field: "enrollmentId", headerName: "Enrollment", flex: 1 },
                  { field: "type", headerName: "Type", flex: 1 },
                  { field: "amount", headerName: "Amount", flex: 1 },
                  { field: "currency", headerName: "Currency", flex: 1 },
                  { field: "status", headerName: "Status", flex: 1 },
                  {
                    field: "transactionId",
                    headerName: "Transaction ID",
                    flex: 1,
                  },
                  { field: "createdAt", headerName: "Date", flex: 1 },
                ]}
                pageSize={10}
                rowsPerPageOptions={[10, 20, 50]}
                disableSelectionOnClick
              />
            </Box>
          </>
        )}
      </Box>
    </>
  );
};

export default AdminPayments;
